import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import { Wallet, Eye, Heart, ShoppingCart, Plus, LogOut, Store, Trash2, Share2, ExternalLink, Shield, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import EditProductModal from "@/components/EditProductModal";
import NotificationsCard from "@/components/NotificationsCard";

function StatCard({ icon: Icon, label, value, color, testId }) {
    return (
        <div className="glass rounded-2xl p-4" data-testid={testId}>
            <Icon className="w-5 h-5 mb-2" style={{ color }} />
            <p className="text-2xl font-display font-black text-white">{value}</p>
            <p className="text-xs font-body text-white/60 mt-1">{label}</p>
        </div>
    );
}

export default function Dashboard() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [products, setProducts] = useState([]);
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        if (!loading && !user) navigate("/login");
    }, [user, loading, navigate]);

    useEffect(() => {
        if (!user) return;
        if (user.role === "seller") {
            api.get("/seller/stats").then((r) => setStats(r.data)).catch(() => {});
            api.get("/products/mine").then((r) => setProducts(r.data)).catch(() => {});
        }
    }, [user]);

    const del = async (id) => {
        if (!window.confirm("Supprimer ce produit ?")) return;
        try {
            await api.delete(`/products/${id}`);
            setProducts((ps) => ps.filter((p) => p.id !== id));
            toast.success("Produit supprimé");
        } catch {
            toast.error("Erreur");
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[#050505] text-white pb-32 grain-bg">
            <div className="max-w-lg mx-auto px-4 pt-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-full overflow-hidden bg-white/10 flex items-center justify-center border border-white/10">
                            {user.picture ? (
                                <img src={user.picture} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <span className="font-display font-black text-xl">{user.name?.charAt(0)}</span>
                            )}
                        </div>
                        <div>
                            <p className="font-display font-bold text-lg leading-tight" data-testid="user-name">
                                {user.shop_name || user.name}
                            </p>
                            <p className="text-xs text-white/50 font-body">{user.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        data-testid="logout-btn"
                        className="w-11 h-11 rounded-full glass flex items-center justify-center hover:bg-white/10"
                    >
                        <LogOut className="w-5 h-5" />
                    </button>
                </div>

                {user.is_admin && (
                    <Link
                        to="/admin"
                        data-testid="admin-link"
                        className="glass rounded-2xl p-4 mb-5 flex items-center gap-3 hover:border-[#FF5722] transition-colors"
                    >
                        <div className="w-11 h-11 rounded-xl bg-[#FF5722] flex items-center justify-center shrink-0">
                            <Shield className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <p className="font-display font-bold">Espace Admin</p>
                            <p className="text-xs text-white/60 font-body">Gérer utilisateurs, produits, catégories</p>
                        </div>
                        <ExternalLink className="w-5 h-5 text-white/50" />
                    </Link>
                )}

                {user.role !== "seller" ? (
                    <div className="glass rounded-3xl p-6 mb-6" data-testid="become-seller-card">
                        <Store className="w-10 h-10 text-[#FF5722] mb-3" />
                        <h2 className="font-display font-black text-2xl mb-1">Devenir vendeur</h2>
                        <p className="text-white/70 font-body text-sm mb-4">
                            Publiez vos produits en vidéo et recevez des commandes WhatsApp directement.
                        </p>
                        <Button
                            onClick={() => navigate("/onboarding")}
                            data-testid="start-onboarding-btn"
                            className="w-full orange-btn h-12 rounded-full font-body font-bold"
                        >
                            Commencer
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* Push notifications */}
                        <NotificationsCard />

                        {/* Shop link */}
                        {user.shop_slug && (
                            <div className="glass rounded-2xl p-4 mb-5 flex items-center justify-between gap-3" data-testid="shop-link-card">
                                <div className="min-w-0">
                                    <p className="text-[11px] uppercase tracking-widest text-white/50 font-body">Ma page publique</p>
                                    <p className="font-display font-bold text-base truncate">@{user.shop_slug}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        data-testid="copy-shop-link-btn"
                                        onClick={async () => {
                                            const url = `${window.location.origin}/shop/${user.shop_slug}`;
                                            try {
                                                if (navigator.share) {
                                                    await navigator.share({ title: user.shop_name, text: `Découvrez ${user.shop_name} sur VIDMAR`, url });
                                                } else {
                                                    await navigator.clipboard.writeText(url);
                                                    toast.success("Lien copié !");
                                                }
                                            } catch (err) {
                                                console.debug("Share cancelled", err?.name);
                                            }
                                        }}
                                        className="w-11 h-11 rounded-full bg-[#FF5722] flex items-center justify-center active:scale-95"
                                    >
                                        <Share2 className="w-5 h-5 text-white" />
                                    </button>
                                    <Link
                                        to={`/shop/${user.shop_slug}`}
                                        data-testid="view-shop-btn"
                                        className="w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center active:scale-95"
                                    >
                                        <ExternalLink className="w-5 h-5" />
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* Credit balance */}
                        <div className="glass rounded-3xl p-5 mb-5 relative overflow-hidden">
                            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-[#FFC107]/20 blur-2xl" />
                            <div className="relative">
                                <div className="flex items-center justify-between">
                                    <p className="text-xs uppercase tracking-widest text-white/60 font-body">Solde crédits</p>
                                    <Wallet className="w-5 h-5 text-[#FFC107]" />
                                </div>
                                <p className="mt-2 font-display font-black text-5xl text-white" data-testid="credit-balance">
                                    {stats?.credits ?? user.credits ?? 0}
                                </p>
                                <p className="text-xs text-white/50 font-body mt-1">1 crédit = 1 demande WhatsApp reçue</p>
                                <Link
                                    to="/wallet"
                                    data-testid="recharge-btn"
                                    className="mt-4 inline-flex h-11 px-5 items-center rounded-full bg-[#FFC107] text-black font-body font-bold text-sm active:scale-95"
                                >
                                    Recharger
                                </Link>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            <StatCard icon={Eye} label="Vues" value={stats?.total_views ?? 0} color="#FFFFFF" testId="stat-views" />
                            <StatCard icon={ShoppingCart} label="Commandes" value={stats?.total_orders ?? 0} color="#25D366" testId="stat-orders" />
                            <StatCard icon={Heart} label="Likes" value={stats?.total_likes ?? 0} color="#FF5722" testId="stat-likes" />
                            <StatCard icon={Store} label="Produits" value={stats?.products_count ?? 0} color="#FFC107" testId="stat-products" />
                        </div>

                        {/* Products */}
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-display font-black text-2xl">Mes produits</h2>
                            <Link
                                to="/upload"
                                data-testid="add-product-btn"
                                className="w-10 h-10 orange-btn rounded-full flex items-center justify-center"
                            >
                                <Plus className="w-5 h-5" />
                            </Link>
                        </div>

                        {products.length === 0 ? (
                            <div className="glass rounded-2xl p-6 text-center">
                                <p className="text-white/60 font-body text-sm">Aucun produit publié. Cliquez sur + pour commencer.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3">
                                {products.map((p) => (
                                    <div
                                        key={p.id}
                                        data-testid={`my-product-${p.id}`}
                                        className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-white/5 group"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/watch/${p.id}`)}
                                            data-testid={`open-product-${p.id}`}
                                            className="absolute inset-0 w-full h-full text-left"
                                            aria-label={`Voir ${p.title}`}
                                        >
                                            <img src={fileUrl(p.image_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent p-3 flex flex-col justify-end">
                                                <p className="font-display font-bold text-sm text-white line-clamp-1">{p.title}</p>
                                                <p className="text-[11px] text-white/80 font-body">{Number(p.price).toLocaleString("fr-FR")} FCFA</p>
                                                <div className="flex gap-3 mt-1 text-[10px] text-white/70 font-body">
                                                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{p.views}</span>
                                                    <span className="flex items-center gap-1"><ShoppingCart className="w-3 h-3" />{p.orders}</span>
                                                </div>
                                            </div>
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setEditing(p); }}
                                            data-testid={`edit-product-${p.id}`}
                                            className="absolute top-2 right-11 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center hover:bg-[#FF5722] transition-colors z-10"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); del(p.id); }}
                                            data-testid={`delete-product-${p.id}`}
                                            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center hover:bg-red-600 transition-colors z-10"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
            <EditProductModal
                product={editing}
                open={!!editing}
                onClose={() => setEditing(null)}
                onUpdated={(updated) => setProducts((ps) => ps.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)))}
            />
            <BottomNav />
        </div>
    );
}
